import type { AdminClientContext, AdminHttpClient } from './client.js';

type MethodKeys =
  | 'listPartners'
  | 'createPartner'
  | 'getPartner'
  | 'updatePartner'
  | 'deletePartner'
  | 'listPartnerAdmins'
  | 'grantPartnerAdmin'
  | 'revokePartnerAdmin'
  | 'getPartnerModelScope'
  | 'setPartnerModelScope'
  | 'listPartnerCustomModels'
  | 'registerPartnerCustomModel'
  | 'patchPartnerCustomModel'
  | 'testPartnerCustomModelProvider'
  | 'testPartnerCustomModelLlm'
  | 'deletePartnerCustomModel'
  | 'platformDeployCustomModel'
  | 'platformRevokeCustomModel'
  | 'getPartnerMcpConnectorScope'
  | 'setPartnerMcpConnectorScope'
  | 'listCapabilitySources'
  | 'registerCapabilitySource'
  | 'deleteCapabilitySource'
  | 'setPartnerCapabilitySourceScope'
  | 'partnerListTenants'
  | 'partnerCreateTenant'
  | 'partnerActAsTenant'
  | 'partnerSelf'
  | 'partnerOverview'
  | 'partnerListAdmins'
  | 'partnerGrantAdmin'
  | 'partnerRevokeAdmin'
  | 'partnerSelfModelScope'
  | 'partnerListCustomModels'
  | 'partnerGetTenantAssignment'
  | 'partnerSetTenantAssignment'
  | 'partnerGetTenantMcpConnectors'
  | 'partnerSetTenantMcpConnectors'
  | 'partnerSetTenantSelfSignup'
  | 'partnerSetTenantDeepAgentApprovalTools';

export function buildPartnersMethods(ctx: AdminClientContext) {
  const { transport, token, idemKey } = ctx;

  return {
    listPartners: () => transport.listPartners(token()),
    createPartner: (input, idempotencyKey) =>
      transport.createPartner(token(), {
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    getPartner: (partnerId) => transport.getPartner(token(), partnerId),
    updatePartner: (partnerId, patch, idempotencyKey) =>
      transport.updatePartner(token(), {
        partnerId,
        ...patch,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    deletePartner: (partnerId, confirmName, idempotencyKey) =>
      transport.deletePartner(token(), {
        partnerId,
        confirmName,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    listPartnerAdmins: (partnerId) =>
      transport.listPartnerAdmins(token(), partnerId),
    grantPartnerAdmin: (partnerId, input, idempotencyKey) =>
      transport.grantPartnerAdmin(token(), {
        partnerId,
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    revokePartnerAdmin: async (partnerId, userId, idempotencyKey) => {
      await transport.revokePartnerAdmin(token(), {
        partnerId,
        userId,
        idempotencyKey: idempotencyKey ?? idemKey(),
      });
      return { ok: true };
    },
    getPartnerModelScope: (partnerId) =>
      transport.getPartnerModelScope(token(), partnerId),
    setPartnerModelScope: (partnerId, modelKeys, idempotencyKey) =>
      transport.setPartnerModelScope(token(), {
        partnerId,
        modelKeys,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    listPartnerCustomModels: (partnerId) =>
      transport.listPartnerCustomModels(token(), partnerId),
    registerPartnerCustomModel: (partnerId, definition, idempotencyKey) =>
      transport.registerPartnerCustomModel(token(), {
        partnerId,
        definition,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    patchPartnerCustomModel: (partnerId, modelKey, request, idempotencyKey) =>
      transport.patchPartnerCustomModel(token(), {
        partnerId,
        modelKey,
        request,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    testPartnerCustomModelProvider: (partnerId, request) =>
      transport.testPartnerCustomModelProvider(token(), { partnerId, request }),
    testPartnerCustomModelLlm: (partnerId, request) =>
      transport.testPartnerCustomModelLlm(token(), { partnerId, request }),
    deletePartnerCustomModel: (partnerId, modelKey, idempotencyKey) =>
      transport.deletePartnerCustomModel(token(), {
        partnerId,
        modelKey,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    platformDeployCustomModel: (tenantId, customModelKey, idempotencyKey) =>
      transport.platformDeployCustomModel(token(), {
        tenantId,
        customModelKey,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    platformRevokeCustomModel: (tenantId, customModelKey, idempotencyKey) =>
      transport.platformRevokeCustomModel(token(), {
        tenantId,
        customModelKey,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    getPartnerMcpConnectorScope: (partnerId) =>
      transport.getPartnerMcpConnectorScope(token(), partnerId),
    setPartnerMcpConnectorScope: (partnerId, connectorIds, idempotencyKey) =>
      transport.setPartnerMcpConnectorScope(token(), {
        partnerId,
        connectorIds,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    listCapabilitySources: () => transport.listCapabilitySources(token()),
    registerCapabilitySource: (input, idempotencyKey) =>
      transport.registerCapabilitySource(token(), {
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    deleteCapabilitySource: (id, idempotencyKey) =>
      transport.deleteCapabilitySource(
        token(),
        id,
        idempotencyKey ?? idemKey()
      ),
    setPartnerCapabilitySourceScope: (partnerId, sourceIds, idempotencyKey) =>
      transport.setPartnerCapabilitySourceScope(token(), {
        partnerId,
        sourceIds,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    partnerListTenants: () => transport.partnerListTenants(token()),
    partnerCreateTenant: (input, idempotencyKey) =>
      transport.partnerCreateTenant(token(), {
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    partnerActAsTenant: (tenantId) =>
      transport.partnerActAsTenant(token(), tenantId),
    partnerSelf: () => transport.partnerSelf(token()),
    partnerOverview: (query) => transport.partnerOverview(token(), query),
    partnerListAdmins: () => transport.partnerListAdmins(token()),
    partnerGrantAdmin: (input, idempotencyKey) => transport.partnerGrantAdmin(token(), { ...input, idempotencyKey: idempotencyKey ?? idemKey() }),
    partnerRevokeAdmin: (userId, idempotencyKey) => transport.partnerRevokeAdmin(token(), { userId, idempotencyKey: idempotencyKey ?? idemKey() }),
    partnerSelfModelScope: () => transport.partnerSelfModelScope(token()),
    partnerListCustomModels: () => transport.partnerListCustomModels(token()),
    partnerGetTenantAssignment: (tenantId) =>
      transport.partnerGetTenantAssignment(token(), tenantId),
    partnerSetTenantAssignment: (tenantId, allowedModelIds, idempotencyKey) =>
      transport.partnerSetTenantAssignment(token(), {
        tenantId,
        allowedModelIds,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    partnerGetTenantMcpConnectors: (tenantId) =>
      transport.partnerGetTenantMcpConnectors(token(), tenantId),
    partnerSetTenantMcpConnectors: (
      tenantId,
      activeMcpConnectors,
      idempotencyKey
    ) =>
      transport.partnerSetTenantMcpConnectors(token(), {
        tenantId,
        activeMcpConnectors,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    partnerSetTenantSelfSignup: (tenantId, enabled, idempotencyKey) =>
      transport.partnerSetTenantSelfSignup(token(), {
        tenantId,
        enabled,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    partnerSetTenantDeepAgentApprovalTools: (
      tenantId,
      deepAgentApprovalTools,
      idempotencyKey
    ) =>
      transport.partnerSetTenantDeepAgentApprovalTools(token(), {
        tenantId,
        deepAgentApprovalTools,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
  } satisfies Pick<AdminHttpClient, MethodKeys>;
}
